// BHP-1062: CPU / disk / network graphs via `GET /api/vps/{uuid}/metrics`
// (BHP-1058). Replaces the class component that called the legacy VpsService
// directly and kept the period in Redux.
//
// One query per metric type, because the endpoint takes `resourceType` as a
// parameter — the same three calls the old chart made, now through React Query
// so a period switch is served from cache when you switch back.
import { useMemo } from 'react';
import BaseChart from '../../../components/Chart/BaseChart';
import { toUserMessage } from '../../../shared/http/errorMessage';
import {
  METRIC_PERIODS,
  METRIC_TYPES,
  type MetricPeriod,
  type MetricType,
  toChartSeries
} from '../domain/vpsServerState';
import { useVpsMetrics } from '../application/vpsDetailQueries';

const TITLES: Record<MetricType, string> = {
  cpu: 'CPU',
  disk: 'Disk',
  network: 'Network'
};

export default function VpsMetricsPanel({
  uuid,
  period,
  onPeriodChange
}: {
  uuid: string;
  period: MetricPeriod;
  onPeriodChange: (period: MetricPeriod) => void;
}): JSX.Element {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">Metrics</h3>
        <div className="flex gap-1" role="group" aria-label="Metrics period">
          {METRIC_PERIODS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === period}
              onClick={() => {
                onPeriodChange(option);
              }}
              className={`rounded-md border border-solid px-2 py-1 text-xs font-medium ${
                option === period
                  ? 'border-teal-600 bg-teal-600 text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {METRIC_TYPES.map((type) => (
        <MetricChart key={type} uuid={uuid} type={type} period={period} />
      ))}
    </section>
  );
}

function MetricChart({
  uuid,
  type,
  period
}: {
  uuid: string;
  type: MetricType;
  period: MetricPeriod;
}): JSX.Element {
  const query = useVpsMetrics(uuid, type, period);
  // Memoised because BaseChart keys its option effect on these arrays; a fresh
  // identity per render would re-apply the option on every pass (BHP-1103).
  const series = useMemo(
    () => toChartSeries(query.data, type),
    [query.data, type]
  );

  if (query.isError) {
    return (
      <p
        role="alert"
        className="rounded border border-solid border-amber-200 bg-amber-50 p-2 text-sm text-amber-900"
      >
        {toUserMessage(query.error, `${TITLES[type]} metrics are unavailable.`)}
      </p>
    );
  }

  if (query.isPending) {
    return (
      <p className="text-sm text-slate-500">Loading {TITLES[type]} metrics…</p>
    );
  }

  // Hetzner omits series for hardware a server does not have, so an empty chart
  // is a normal outcome rather than a failure.
  if (series.points.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No {TITLES[type]} data for this period.
      </p>
    );
  }

  return (
    <BaseChart
      title={TITLES[type]}
      dates={series.labels}
      data={series.points[0].values}
    />
  );
}
